import React from "react";
import { motion } from "framer-motion";
import { PhoneCall, FileBadge, LineChart, ArrowUpRight, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { waLink } from "../lib/whatsapp";
import { openBooking } from "../lib/booking";
import WhatsAppIcon from "./WhatsAppIcon";
import Hud from "./HudOverlay";

const PATHS = [
  {
    id: "strategy-call",
    icon: PhoneCall,
    title: "Book a $250 Strategy Call",
    from: "$250 · credited",
    featured: true,
    desc: "The first step for every founder. 30 minutes, straight answers, a written launch map.",
    points: [
      "Jurisdiction, structure & licence mapped to your model",
      "Realistic all-in budget — our fees + every third-party cost",
      "$250 credited in full toward any engagement",
    ],
    wa: "Hi Ah-Jin Group — I'd like to book the $250 consultation (30 min strategy call).",
    cta: "Book a $250 Strategy Call",
  },
  {
    id: "licensing",
    icon: FileBadge,
    title: "Incorporation & Licensing",
    from: "From $6,500",
    page: "/incorporation",
    desc: "The right entity and licence, filed properly and bank-ready.",
    points: [
      "UAE, Saint Lucia, Seychelles, BVI & more",
      "Licence file preparation & regulator dialogue",
      "Banking & PSP onboarding",
    ],
    cta: "View Incorporation Options",
  },
  {
    id: "launch",
    icon: LineChart,
    title: "Brokerage / Prop Firm Setup",
    from: "From $18,000",
    page: "/brokerage-launch",
    desc: "A live, operating business — built end to end by one team.",
    points: [
      "Platform — MT4/MT5, cTrader, Match-Trader",
      "Liquidity, CRM & payment rails",
      "Challenge model & payouts for prop firms",
    ],
    wa: "Hi Ah-Jin Group — I want to launch a brokerage / prop firm.",
    cta: "Ask a question",
  },
];

const BuyerPaths = () => (
  <section id="paths" className="relative py-10 sm:py-14 overflow-hidden" data-testid="buyer-paths-section">
    <div className="absolute inset-0 bg-[#050b1c]/85" />
    <div className="absolute right-0 top-0 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(126,34,206,0.20) 0%, transparent 70%)" }} />

    <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
      <div className="flex items-center gap-3 text-[11px] tracking-[0.5em] text-purple-400 uppercase">
        <span className="w-8 h-px bg-purple-500/60 neon-dash" />
        Choose your path
      </div>
      <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-[1.05] tracking-tight">
        Start with a call. <em className="not-italic gold-text">Leave with a map.</em>
      </h2>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PATHS.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{ y: -6 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.75, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`group lux-card trace-border relative overflow-hidden rounded-2xl p-5 sm:p-6 flex flex-col ${
                p.featured ? "border border-[#E5C56F]/40 shadow-[0_0_36px_rgba(229,197,111,0.10)]" : ""
              }`}
              data-testid={`path-${p.id}`}
            >
              <Hud />
              <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full pointer-events-none" style={{ background: p.featured ? "radial-gradient(circle, rgba(229,197,111,0.16) 0%, transparent 70%)" : "radial-gradient(circle, rgba(147,51,234,0.22) 0%, transparent 70%)" }} />

              <div className="relative flex items-center justify-between">
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center icon-glow ${p.featured ? "border-[#E5C56F]/40 bg-[#E5C56F]/10 text-[#E5C56F]" : "border-purple-500/30 bg-purple-600/10 text-purple-300"}`}>
                  <Icon size={18} strokeWidth={1.4} />
                </div>
                <Link
                  to="/pricing"
                  className="text-[10px] tracking-[0.25em] uppercase gold-text hover:opacity-80 transition-opacity"
                  data-testid={`path-${p.id}-pricing-link`}
                >
                  {p.from}
                </Link>
              </div>

              <h3 className="relative font-display text-xl sm:text-2xl mt-4 text-zinc-100 leading-tight">
                {p.title}
              </h3>
              <p className="relative mt-2 text-[13px] text-zinc-400 leading-relaxed">{p.desc}</p>

              <ul className="relative mt-4 space-y-2 flex-1">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-[13px] text-zinc-200/90">
                    <span className={`mt-1.5 inline-block w-1 h-1 rounded-full shrink-0 ${p.featured ? "bg-[#E5C56F]" : "bg-purple-400"}`} />
                    {pt}
                  </li>
                ))}
              </ul>

              <div className="relative mt-6 pt-4 border-t border-white/10 flex items-center gap-3 flex-wrap">
                {p.id === "strategy-call" ? (
                  <button
                    type="button"
                    onClick={() => openBooking("buyer-paths")}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1a1405] hover:brightness-110 transition-all"
                    data-testid={`path-${p.id}-whatsapp-btn`}
                  >
                    <CalendarCheck size={14} />
                    {p.cta}
                  </button>
                ) : p.wa ? (
                  <a
                    href={waLink(p.wa)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={
                      p.featured
                        ? "inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1a1405] hover:brightness-110 transition-all"
                        : "inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-purple-600/10 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-purple-200 hover:border-purple-400/60 hover:bg-purple-600/20 transition-all"
                    }
                    data-testid={`path-${p.id}-whatsapp-btn`}
                  >
                    <WhatsAppIcon size={14} />
                    {p.cta}
                  </a>
                ) : (
                  <Link
                    to={p.page}
                    className="inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-purple-600/10 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-purple-200 hover:border-purple-400/60 hover:bg-purple-600/20 transition-all"
                    data-testid={`path-${p.id}-cta-btn`}
                  >
                    {p.cta}
                  </Link>
                )}
                {p.page && p.wa && (
                  <Link
                    to={p.page}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] text-purple-300 hover:text-purple-200 transition-colors"
                    data-testid={`path-${p.id}-learn-more`}
                  >
                    Learn more
                    <ArrowUpRight size={12} strokeWidth={1.6} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

export default BuyerPaths;
